import React from "react";
import _ from "underscore";
import { Box, Button, Checkbox, Chip, ListItemText, MenuItem, Select, SelectChangeEvent, Switch, Typography, useMediaQuery, useTheme } from "@mui/material";
import { FRIEND_TOPIC_KEY, TOPICS } from "../../shared/topics";
import { Languages } from "../../shared/language/languages";
import { t } from "i18next";

const PALETTE = ["#D36667", "#A7C577", "#7F97C2", "#CA8755", "#C6C969", "#9B8EC4"];

interface Props {
  selectedTopics: string[];
  onTopicsChange: (topics: string[]) => void;
  nsfwEnabled: boolean;
  onNsfwChange: (nsfw: boolean) => void;
  includeLowQuality: boolean;
  onIncludeLowQualityChange: (val: boolean) => void;
  language: string[];
  onLanguageChange: (lang: string[]) => void;
  /** "Rank your friends" has no content without the friend pool, so the selector owns turning it on. */
  friendPoolEnabled: boolean;
  onFriendPoolEnabledChange: (enabled: boolean) => void;
  disabled?: boolean;
}

export default function TopicSelector({
  selectedTopics,
  onTopicsChange,
  nsfwEnabled,
  onNsfwChange,
  includeLowQuality,
  onIncludeLowQualityChange,
  language,
  onLanguageChange,
  friendPoolEnabled,
  onFriendPoolEnabledChange,
  disabled,
}: Props) {
  const isMobile = useMediaQuery(useTheme().breakpoints.down("md"));

  const toggleTopic = (key: string) => {
    if (disabled) return;
    // Clicking sex_intimacy auto-enables NSFW
    if (key === "sex_intimacy" && !nsfwEnabled) {
      onNsfwChange(true);
    }
    // Same idea for friends: its cues *are* the friend pool, so picking it with the pool off
    // would quietly select a topic with no content.
    if (key === FRIEND_TOPIC_KEY && !friendPoolEnabled) {
      onFriendPoolEnabledChange(true);
    }
    if (selectedTopics.includes(key)) {
      onTopicsChange(selectedTopics.filter((tp) => tp !== key));
    } else {
      onTopicsChange([...selectedTopics, key]);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {TOPICS.map((topic, i) => {
          const isSelected = selectedTopics.includes(topic.key);
          const isDisabledChip = disabled;
          const color = PALETTE[i % PALETTE.length];

          return (
            <Chip
              key={topic.key}
              label={t(topic.i18nKey)}
              size="small"
              variant={isSelected ? "filled" : "outlined"}
              onClick={() => !isDisabledChip && toggleTopic(topic.key)}
              sx={{
                cursor: isDisabledChip ? "default" : "pointer",
                opacity: isSelected ? 1 : 0.45,
                ...(isSelected
                  ? {
                      bgcolor: color,
                      color: "#fff",
                      "&:hover": { bgcolor: color, opacity: 0.85 },
                    }
                  : {
                      borderColor: "divider",
                      "&:hover": isDisabledChip ? {} : { opacity: 0.7 },
                    }),
                transition: "all 0.15s ease",
              }}
            />
          );
        })}
      </Box>
      {selectedTopics.includes(FRIEND_TOPIC_KEY) && !friendPoolEnabled && (
        <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
          {t("topic_rank_friends_needs_pool")}
        </Typography>
      )}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t("topic_18_plus")}
            </Typography>
            <Switch
              size="small"
              checked={nsfwEnabled}
              onChange={(_, checked) => {
                onNsfwChange(checked);
                if (!checked && selectedTopics.includes("sex_intimacy")) {
                  onTopicsChange(selectedTopics.filter((tp) => tp !== "sex_intimacy"));
                }
              }}
              disabled={disabled}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {t("topic_low_quality")}
            </Typography>
            <Switch
              size="small"
              checked={includeLowQuality}
              onChange={(_, checked) => onIncludeLowQualityChange(checked)}
              disabled={disabled}
            />
          </Box>
          <Select
            size="small"
            multiple
            value={language}
            onChange={(e: SelectChangeEvent<string[]>) => {
              const val = e.target.value;
              if (typeof val !== "string" && val.includes("deselectAll")) {
                onLanguageChange([]);
              } else if (typeof val !== "string" && val.includes("selectAll")) {
                onLanguageChange(Object.keys(Languages).map((k) => Languages[k].id));
              } else {
                onLanguageChange(typeof val === "string" ? val.split(",") : val);
              }
            }}
            renderValue={(selected) => {
              if (selected.length === 0) return "🌍";
              const allLangs = _.toArray(Languages);
              const maxShow = isMobile ? 1 : 3;
              const flags = selected.slice(0, maxShow).map((id) => allLangs.find((l) => l.id === id)?.name ?? id);
              return selected.length > maxShow ? flags.join("") + "…" : flags.join("");
            }}
            displayEmpty
            disabled={disabled}
            sx={{ fontSize: "0.85rem", height: 28, minWidth: 56 }}
          >
            <MenuItem value={language.length !== 0 ? "deselectAll" : "selectAll"} sx={{ fontSize: "0.85rem" }}>
              {language.length !== 0 ? t("deselect_all") : t("select_all")}
            </MenuItem>
            {_.toArray(Languages).map((lang) => (
              <MenuItem key={lang.id} value={lang.id} sx={{ display: "inline-flex", width: "20%", fontSize: "0.85rem" }}>
                <Checkbox size="small" checked={language.includes(lang.id)} sx={{ p: 0, mr: 0.5 }} />
                {lang.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {!disabled && (
          <Button
            size="small"
            variant="text"
            sx={{ fontSize: "0.75rem", textTransform: "none", minWidth: 0, p: "2px 8px" }}
            onClick={() => {
              const allKeys = TOPICS.filter((tp) => nsfwEnabled || tp.key !== "sex_intimacy").map((tp) => tp.key);
              const allSelected = allKeys.every((k) => selectedTopics.includes(k));
              if (!allSelected && !friendPoolEnabled) onFriendPoolEnabledChange(true);
              onTopicsChange(allSelected ? [] : allKeys);
            }}
          >
            {TOPICS.filter((tp) => nsfwEnabled || tp.key !== "sex_intimacy").every((tp) => selectedTopics.includes(tp.key))
              ? t("deselect_all")
              : t("select_all")}
          </Button>
        )}
      </Box>
    </Box>
  );
}
